import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, FC, useCallback, useContext, useMemo, useState } from 'react';
import {
  DeliveryMethodDto,
  DirectSaleRequest,
  DirectSaleRequestPaymentProviderEnum,
  InitiatedCartPaymentDto,
  ListActivePaymentDto,
  PaymentMethodDto,
  PaymentState,
} from '../../generated-api';
import config from '../config';
import { useActivePayments } from '../hooks/useActivePayments';
import { useMutateCartPayV3 } from '../hooks/useCartApi/useMutatePayCartV2';
import useDeviceId from '../hooks/useDeviceId';
import { useAutomaticPrintingPlacement } from '../hooks/useiOSPlacementId';

import FocusedPaymentDialog from '../pages/Menu/checkout-sidebar/Cart/payment/FocusedPaymentDialog/FocusedPaymentDialog';
import { toPayCartDto } from '../utils/cart';
import { sendIosMessageCashPaymentSuccess } from '../utils/checkout';
import { useLocation } from './LocationContext';
import { useErrorHandler } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from './ScreenSizeContext';
import softpayClient from '../utils/softpay/SoftPayClient';

import { useTipDialogRemake } from '../hooks/useTipDialog';
import useCreatePendingPayment from '../hooks/usePendingPaymentApi/useCreatePendingPayment';
import { getEffectiveTotalPrice } from '../hooks/useTotalPrice';
import { useTerminalModeEnabled } from '../hooks/useTerminalModeEnabled';
import useSetNewCart from '../hooks/useSetNewCart';
import { useCartStore } from './cart/CartStore';
import { useMutateInitiateDirectSale } from '../hooks/useInitiatePayment';
import { useDiscounts } from './DiscountStore';
import { setPaymentTracingActive } from '../tracing';

type InitiatePaymentArgs = {
  paymentMethod: PaymentMethodDto;
  deliveryMethod: DeliveryMethodDto;
  tableNumber?: string;
  comment?: string;
  onSuccess?: () => void;
  onError?: () => void;
  dynamicFields?: { [key: string]: string };
  giftCardPhoneNumber?: string;
  tipAmount?: number;
  vatType: 'EAT_IN' | 'TAKEAWAY';
};
export type PaymentContextType = {
  initiatePayment: (initiatePaymentArgs: InitiatePaymentArgs) => void;
  activePayments?: ListActivePaymentDto[];
  setFocusedPayment: (activePayment: ListActivePaymentDto) => void;
};

export const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

interface ProviderProps {
  children?: React.ReactNode;
}

export const PaymentContextProvider: FC<ProviderProps> = ({ children }) => {
  const payCartMutation = useMutateCartPayV3();
  const throwErrorToBoundary = useErrorHandler();
  const { location } = useLocation();
  const discounts = useDiscounts();

  const getCartSnapshot = useCartStore((state) => state.getCartSnapshot);

  const setNewCart = useSetNewCart();

  const queryClient = useQueryClient();
  const deviceId = useDeviceId();
  const { defaultPlacement } = useAutomaticPrintingPlacement();
  const navigate = useNavigate();
  const { isPhone } = useScreenSize();

  const { createPendingPayment } = useCreatePendingPayment();
  const initiateDirectSaleMutation = useMutateInitiateDirectSale();

  const isTerminalModeEnabled = useTerminalModeEnabled();

  const [focusedPayment, setFocusedPayment] = useState<ListActivePaymentDto>();

  const { promptForTips, TipsDialogUI } = useTipDialogRemake();

  const onPaymentCaptured = useCallback(() => {
    if (isPhone) {
      navigate(`/${location.locationId}`);
    }
  }, [isPhone, location.locationId, navigate]);

  const { activePayments, addActivePayment, removeActivePayment, handlePaidOrFailedPayment } = useActivePayments({
    focusedPayment,
    setFocusedPayment,
    onPaymentCaptured,
  });

  const handleInitiatePaymentSuccess = useCallback(
    (payment: ListActivePaymentDto, onSuccess?: () => void) => async (data: InitiatedCartPaymentDto) => {
      if (payment.paymentMethod.paymentMethod === 'CASH') {
        await sendIosMessageCashPaymentSuccess(
          data.paymentAmount,
          data.paymentReference,
          location.locationId,
          location.currency,
          throwErrorToBoundary
        );
      }

      if (
        payment.paymentMethod.provider === 'SOFTPAY' &&
        !payment.paymentMethod.isUsingHub &&
        data.requestId &&
        softpayClient
      ) {
        softpayClient?.processPending(data.requestId, 3000, 'auto');
      }

      if (config.env.includes('local')) {
        try {
          await navigator.clipboard.writeText(data.paymentReference);
        } catch (error) {
          console.log(error);
        }
      }

      const newPayment: ListActivePaymentDto = {
        paymentReference: data.paymentReference,
        paymentState: data.paymentState,
        paymentMethod: payment.paymentMethod,
        cart: payment.cart,
        deviceId,
      };

      handlePaidOrFailedPayment(newPayment);

      setNewCart();

      onSuccess && onSuccess();
    },
    [deviceId, handlePaidOrFailedPayment, location.currency, location.locationId, setNewCart, throwErrorToBoundary]
  );

  const handlePaymentError = useCallback(
    (payment: ListActivePaymentDto, onError?: () => void) => () => {
      removeActivePayment(payment);
      onError && onError();
    },
    [removeActivePayment]
  );

  const initiatePayment = useCallback(
    async (props: InitiatePaymentArgs) => {
      const {
        paymentMethod,
        deliveryMethod,
        tableNumber,
        comment,
        onSuccess,
        onError,
        dynamicFields,
        giftCardPhoneNumber,
        tipAmount,
        vatType,
      } = props;

      const cart = getCartSnapshot();
      const effectiveTotal = getEffectiveTotalPrice(cart);

      const promptTips = paymentMethod.askForTip && paymentMethod.provider === 'SOFTPAY' && isTerminalModeEnabled;
      const terminalTipAmount = await promptForTips(promptTips, effectiveTotal, location.currency);

      const newPayment = {
        paymentMethod,
        cart,
        paymentState: PaymentState.INITIATING,
        deviceId: deviceId,
      };
      addActivePayment(newPayment);

      const resolvedDiscount = cart?.discountId
        ? discounts.find((discount) => discount.id === cart.discountId)
        : undefined;

      const payCartDto = toPayCartDto(
        {
          paymentMethod,
          deliveryMethod,
          tableNumber,
          tipAmount: terminalTipAmount || tipAmount || 0,
          comment,
          placementId: defaultPlacement,
          discount: resolvedDiscount,
          deviceId,
          dynamicFields,
          giftCardPhoneNumber,
          vatType: vatType,
        },
        cart
      );

      const initPendingPayment = () => {
        createPendingPayment({
          amount: effectiveTotal,
          paymentMethodId: paymentMethod.id,
          deviceId,
          payCartDto,
          locationId: location.locationId,
          askForTip: paymentMethod.askForTip,
          currency: location.currency,
          onSuccess: (response) => {
            setPaymentTracingActive(false);
            const mappedResponse: ListActivePaymentDto = {
              deviceId,
              paymentMethod: paymentMethod,
              paymentReference: response.correlationId,
              paymentState: PaymentState.AWAITING_PAYMENT,
              cart: response.cartDto,
            };

            setNewCart();

            const handleSuccess = handleInitiatePaymentSuccess(mappedResponse, onSuccess);

            handleSuccess({
              paymentState: PaymentState.AWAITING_PAYMENT,
              paymentReference: response.correlationId,
              cartDto: response.cartDto!,
              paymentAmount: effectiveTotal,
            });
          },
          onError: () => {
            setPaymentTracingActive(false);
            handlePaymentError(newPayment, onError)();
          },
        });
      };

      const initHubPayment = () => {
        initiateDirectSaleMutation.mutate(
          {
            locationId: location.locationId,
            deviceId: deviceId,
            tableNumber: tableNumber,
            deliveryMethodId: deliveryMethod.id,
            cart: cart,
            vatType: vatType,
            discountCode: resolvedDiscount?.discountCodes[0],
            giftCardPhoneNumber: giftCardPhoneNumber,
            placementId: defaultPlacement,
            paymentMethodId: paymentMethod.id,
            paymentProvider: DirectSaleRequestPaymentProviderEnum.SOFTPAY,
            paymentMethod: paymentMethod,
            askForTip: paymentMethod.askForTip,
          } as DirectSaleRequest,
          {
            onSuccess: (response) => {
              handleInitiatePaymentSuccess(
                newPayment,
                onSuccess
              )({
                cartDto: cart,
                paymentState: PaymentState.AWAITING_PAYMENT,
                paymentAmount: -1, // only needed for cash payments
                ...response,
              });
            },
            onError: handlePaymentError(newPayment, onError),
            onSettled: () => {
              setPaymentTracingActive(false);
              queryClient.refetchQueries({
                queryKey: ['active-payments', location.locationId],
              });
            },
          }
        );
      };

      const initPayment = () => {
        payCartMutation.mutate(
          {
            payCartDto,
          },
          {
            onSuccess: handleInitiatePaymentSuccess(newPayment, onSuccess),
            onError: handlePaymentError(newPayment, onError),
            onSettled: () => {
              setPaymentTracingActive(false);
              queryClient.refetchQueries({
                queryKey: ['active-payments', location.locationId],
              });
            },
          }
        );
      };

      const shouldStartPendingPayment =
        paymentMethod.provider === 'SOFTPAY' && paymentMethod.askForTip && !isTerminalModeEnabled;

      setPaymentTracingActive(true);
      // TODO: Differentiate between direct sales, paying a partial/full booking, and paying by amount
      paymentMethod.isUsingHub ? initHubPayment() : shouldStartPendingPayment ? initPendingPayment() : initPayment();
    },
    [
      getCartSnapshot,
      isTerminalModeEnabled,
      promptForTips,
      location.currency,
      discounts,
      location.locationId,
      deviceId,
      addActivePayment,
      defaultPlacement,
      createPendingPayment,
      handlePaymentError,
      setNewCart,
      handleInitiatePaymentSuccess,
      initiateDirectSaleMutation,
      queryClient,
      payCartMutation,
    ]
  );

  const contextValue = useMemo(
    () => ({
      initiatePayment,
      activePayments,
      setFocusedPayment,
    }),
    [initiatePayment, activePayments, setFocusedPayment]
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      <>
        {children}
        {!!focusedPayment && (
          <FocusedPaymentDialog
            isOpen={!!focusedPayment}
            removeActivePayment={removeActivePayment}
            onCloseDialog={() => setFocusedPayment(undefined)}
            focusedPayment={focusedPayment}
          />
        )}
        {TipsDialogUI}
      </>
    </PaymentContext.Provider>
  );
};

export const usePaymentContext = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePaymentContext must be used within a PaymentProvider');
  }
  return context;
};
